import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

/**
 * Sélecteur de classe groupé par section, avec recherche.
 *
 * Extrait du parcours d'inscription, qui en était le seul porteur. Le dossier de
 * l'élève en avait besoin pour le changement de classe : le dupliquer aurait
 * laissé deux listes de classes divergentes dans la même application.
 */
const sortByOrderThenName = (a, b) => {
  const orderA = Number(a?.ordre ?? 9999);
  const orderB = Number(b?.ordre ?? 9999);
  if (orderA !== orderB) return orderA - orderB;
  return String(a?.nom || a?.label || '').localeCompare(String(b?.nom || b?.label || ''), 'fr');
};

/**
 * `hauteur` permet d'aligner ce selecteur sur les champs a cote de lui.
 *
 * Sa hauteur par defaut est `min-h-[44px]`, confortable au doigt, mais la
 * charte pose les champs a `h-9` — et `ChampSelect`, la liste deroulante de
 * reference, s'y tient. Cote a cote, les deux se decalaient de huit pixels.
 *
 * Le defaut ne change pas : les deux autres ecrans qui s'en servent gardent
 * leur hauteur. C'est l'appelant qui demande l'alignement quand il en a besoin.
 */
const GroupedClassDropdown = ({ value, classes, sections, onChange, disabled, hauteur }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const selectedClass = classes.find(classe => String(classe.id) === String(value));

  const groupedClasses = useMemo(() => {
    const bySection = classes.reduce((acc, classe) => {
      const sectionId = classe.section_id || 'sans-section';
      if (!acc[sectionId]) acc[sectionId] = [];
      acc[sectionId].push(classe);
      return acc;
    }, {});

    const knownSections = sections
      .filter(section => bySection[section.id]?.length > 0)
      .sort(sortByOrderThenName)
      .map(section => ({
        id: section.id,
        nom: section.nom,
        classes: bySection[section.id].sort(sortByOrderThenName)
      }));

    const orphanClasses = bySection['sans-section'] || [];
    if (orphanClasses.length > 0) {
      knownSections.push({
        id: 'sans-section',
        nom: 'Sans section',
        classes: orphanClasses.sort(sortByOrderThenName)
      });
    }

    return knownSections;
  }, [classes, sections]);

  const filteredGroups = groupedClasses
    .map(group => ({
      ...group,
      classes: group.classes.filter(classe =>
        String(classe.nom || '').toLowerCase().includes(query.toLowerCase())
      )
    }))
    .filter(group => group.classes.length > 0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative mt-1" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen(prev => !prev);
        }}
        className={`w-full ${hauteur || 'min-h-[44px] py-2.5'} px-4 border border-gray-200 rounded-lg bg-white text-left flex items-center justify-between gap-3 focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500`}
      >
        <span className={selectedClass ? 'text-gray-900' : 'text-gray-400'}>
          {selectedClass?.nom || 'Sélectionner une classe'}
        </span>
        <ChevronDown size={18} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une classe..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {filteredGroups.length === 0 ? (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">Aucune classe trouvée</div>
            ) : (
              filteredGroups.map(group => (
                <div key={group.id}>
                  <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-[#246990] bg-[#eef7fb] font-semibold">
                    {group.nom}
                  </div>
                  {group.classes.map(classe => (
                    <button
                      key={classe.id}
                      type="button"
                      onClick={() => {
                        onChange(classe.id);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-3 hover:bg-[#f4fafc] ${
                        String(classe.id) === String(value) ? 'bg-[#e7f3f8] text-[#246990]' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-sm font-medium">{classe.nom}</span>
                      {String(classe.id) === String(value) && <Check size={16} />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedClassDropdown;
